import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBrandedImages, useCompanyBranding, useBrandedColors } from '../hooks/useBranding';
import { Eye, EyeOff } from 'lucide-react';
import localeConfig from '../config/locale.config';

const Login = () => {
    const [mobile, setMobile] = useState('');
    const [otp, setOtp] = useState('');
    const [showOtp, setShowOtp] = useState(false);
    const [otpSent, setOtpSent] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [otpLoading, setOtpLoading] = useState(false);
    const [showOtpField, setShowOtpField] = useState(false);
    
    const { login, requestOTP } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Get branded content
    const { loginLogo } = useBrandedImages();
    const { name, tagline } = useCompanyBranding();
    const colors = useBrandedColors();

    // Handle success message from registration
    useEffect(() => {
        if (location.state?.message) {
            setSuccess(location.state.message);
            // Clear the message from location state
            navigate(location.pathname, { replace: true });
        }
    }, [location.state, navigate, location.pathname]);

    const getOtpRequestErrorMessage = (err) => {
        const raw = typeof err?.message === 'string' ? err.message : '';
        const lower = raw.toLowerCase();

        if (
            err?.status === 401 &&
            (lower.includes('not found') || lower.includes('not authorized') || lower.includes('not valid'))
        ) {
            return 'Your account is not active yet. If you have recently registered, your registration is pending approval. You will be able to log in once it is approved.';
        }
        if (err?.status === 0) {
            return raw || 'Network error. Please check your connection.';
        }
        return raw || 'Failed to send OTP. Please try again.';
    };

    const handleRequestOTP = async () => {
        if (!mobile || mobile.length < localeConfig.mobile.minDigits) {
            setError('Please enter a valid mobile number');
            return;
        }

        setError('');
        setOtpLoading(true);

        try {
            await requestOTP(mobile);
            setOtpSent(true);
            setShowOtpField(true);
            setError('');
        } catch (err) {
            console.error('OTP request error:', err);
            setError(getOtpRequestErrorMessage(err));
        } finally {
            setOtpLoading(false);
        }
    };

    const handleLogin = async () => {
        if (!mobile || !otp) {
            setError('Please enter mobile number and OTP');
            return;
        }

        if (otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }

        setError('');
        setLoading(true);

        try {
            const loginResult = await login(mobile, otp);
            console.log('Login successful:', loginResult);
            navigate('/dashboard');
        } catch (err) {
            console.error('Login error:', err);
            
            let errorMessage = 'Login failed. Please try again.';
            
            if (err.status === 401) {
                errorMessage = 'Invalid OTP. Please check and try again.';
            } else if (err.status === 400) {
                errorMessage = err.message || 'Invalid login data. Please check your input.';
            } else if (err.status === 500) {
                errorMessage = 'Server error. Please try again later.';
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleRegister = () => {
        navigate('/register');
    };

    const formatMobileNumber = (value) => {
        // Remove all non-digits
        const digits = value.replace(/\D/g, '');
        const limitedDigits = digits.slice(0, localeConfig.mobile.maxDigits);
        return limitedDigits;
    };

    const handleMobileChange = (e) => {
        const formatted = formatMobileNumber(e.target.value);
        setMobile(formatted);
        // Reset OTP state when mobile number changes
        if (otpSent) {
            setOtpSent(false);
            setShowOtpField(false);
            setOtp('');
        }
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setOtp(value);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            if (!otpSent) {
                handleRequestOTP();
            } else {
                handleLogin();
            }
        }
    };

    return (
        <div 
            className="min-h-screen flex items-center justify-center p-4"
            style={{
                backgroundColor: colors.secondary
            }}
        >
            <div className="bg-white rounded-2xl shadow-2xl p-5 sm:p-8 w-full max-w-md">
                <div className="text-center mb-6 sm:mb-8">
                    <div className="inline-flex items-center justify-center rounded-full mb-4">

                        {loginLogo && (
                            <img 
                                src={loginLogo} 
                                alt="Company Logo" 
                            />
                        )}
                    </div>
                    <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{name}</h1>
                    <p className="text-gray-600 mt-2">{tagline}</p> 
                </div>

                <div className="space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-lg text-sm">
                            {success}
                        </div>
                    )}

                    <div>
                        <label className="block text-1x1 font-normal text-black mb-1">
                            Mobile Number
                        </label>
                        <input
                            type="tel"
                            value={mobile}
                            onChange={(e) => {
                                handleMobileChange(e);
                                if (error) setError(''); // Clear error when user starts typing
                            }}
                            onKeyPress={handleKeyPress}
                            className="outline-none w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                            style={{
                                '--tw-ring-color': colors.primary
                            }}
                            placeholder={localeConfig.mobile.placeholder}
                            disabled={loading || otpLoading}
                        />
                        {mobile && mobile.length < localeConfig.mobile.minDigits && (
                            <p className="mt-1 text-xs text-red-600">
                                {localeConfig.mobile.errorText}
                            </p>
                        )}
                    </div>

                    {/* OTP Field */}
                    {showOtpField && (
                        <div>
                            <label className="block text-1x1 font-normal text-black mb-1">
                                Enter OTP
                            </label>
                            <div className="relative">
                                <input
                                    type={showOtp ? "text" : "password"}
                                    value={otp}
                                    onChange={(e) => {
                                        handleOtpChange(e);
                                        if (error) setError(''); // Clear error when user starts typing
                                    }}
                                    onKeyPress={handleKeyPress}
                                    className="outline-none w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent pr-10"
                                    style={{
                                        '--tw-ring-color': colors.primary
                                    }}
                                    placeholder="Enter 6-digit OTP"
                                    disabled={loading}
                                    maxLength={6}
                                />
                                <button
                                    type="button"
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    onClick={() => setShowOtp(!showOtp)}
                                >
                                    {showOtp ? (
                                        <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    ) : (
                                        <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                                    )}
                                </button>
                            </div>
                            {otp && otp.length < 6 && (
                                <p className="mt-1 text-xs text-red-600">
                                    Please enter a 6-digit OTP
                                </p>
                            )}
                        </div>
                    )}

                    {/* Request OTP Button */}
                    {!otpSent && (
                        <button
                            onClick={handleRequestOTP}
                            disabled={!mobile || mobile.length < localeConfig.mobile.minDigits || otpLoading}
                            className="w-full text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                            style={{
                                backgroundColor: colors.primary
                            }}
                        >
                            {otpLoading ? 'Sending OTP...' : 'Send OTP'}
                        </button>
                    )}

                    {/* Login Button */}
                    {otpSent && (
                        <button
                            onClick={handleLogin}
                            disabled={!mobile || !otp || otp.length !== 6 || loading}
                            className="w-full text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                            style={{
                                backgroundColor: colors.primary
                            }}
                        >
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    )}

                    {/* Resend OTP */}
                    {showOtpField && (
                        <div className="text-center">
                            <button
                                onClick={handleRequestOTP}
                                disabled={otpLoading}
                                className="text-sm disabled:opacity-50 hover:opacity-80 transition-opacity"
                                style={{
                                    color: colors.primary
                                }}
                            >
                                Resend OTP
                            </button>
                        </div>
                    )}

                    {/* Register Button */}
                    <button
                        onClick={handleRegister}
                        disabled={loading || otpLoading}
                        className="w-full text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
                        style={{
                            backgroundColor: colors.secondary
                        }}
                    >
                        Register as New Vendor
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Login; 